/**
 * AuditLog - Journal d'audit des changements RBAC
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  History, Search, RefreshCw, Filter, Calendar, 
  User, Shield, Download, ArrowRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Actions avec labels et couleurs
const ACTION_CONFIG = {
  'role_change': { label: 'Changement de rôle', color: 'bg-blue-100 text-blue-700' },
  'bulk_assign': { label: 'Assignation en masse', color: 'bg-purple-100 text-purple-700' },
  'create': { label: 'Création', color: 'bg-green-100 text-green-700' },
  'update': { label: 'Modification', color: 'bg-amber-100 text-amber-700' },
  'delete': { label: 'Suppression', color: 'bg-red-100 text-red-700' },
  'export': { label: 'Export', color: 'bg-gray-100 text-gray-700' },
  'import': { label: 'Import', color: 'bg-indigo-100 text-indigo-700' },
};

const AuditLog = () => {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 20,
    total: 0,
    hasMore: false
  });

  useEffect(() => {
    fetchAuditLog();
    fetchStats();
  }, [filters, pagination.offset]);

  const fetchAuditLog = async () => {
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });

      if (filters.action && filters.action !== 'all') params.append('action_type', filters.action);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);

      const response = await axios.get(`${API}/rbac/audit-log?${params}`);
      setEntries(response.data.entries);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        hasMore: response.data.hasMore
      }));
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger le journal d'audit",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/rbac/audit-log/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadge = (action) => {
    const config = ACTION_CONFIG[action] || { label: action, color: 'bg-gray-100 text-gray-700' };
    return (
      <Badge className={`${config.color} font-normal`}>
        {config.label}
      </Badge>
    );
  };

  const renderChanges = (changes) => {
    if (!changes) return '-';
    
    if (changes.oldRole && changes.newRole) {
      return (
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="outline" className="font-normal">{changes.oldRole}</Badge>
          <ArrowRight className="w-4 h-4 text-gray-400" />
          <Badge className="bg-emerald-100 text-emerald-700 font-normal">
            {changes.newRoleName || changes.newRole}
          </Badge>
        </div>
      );
    }

    if (changes.format) {
      return <span className="text-sm">Format: {changes.format}</span>;
    }

    return (
      <span className="text-sm text-gray-500">
        {JSON.stringify(changes).slice(0, 50)}...
      </span>
    );
  };

  const handlePrevPage = () => {
    setPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      setPagination(prev => ({
        ...prev,
        offset: prev.offset + prev.limit
      }));
    }
  };

  const clearFilters = () => {
    setFilters({ action: 'all', dateFrom: '', dateTo: '' });
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  if (loading && entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-gray-500">Total entrées</div>
            </CardContent>
          </Card>
          {Object.entries(stats.byAction || {}).slice(0, 3).map(([action, count]) => (
            <Card key={action}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{count}</div>
                <div className="text-sm text-gray-500">
                  {ACTION_CONFIG[action]?.label || action}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium">Filtres:</span>
            </div>

            <Select
              value={filters.action}
              onValueChange={(value) => setFilters(prev => ({ ...prev, action: value }))}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Type d'action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les actions</SelectItem>
                {Object.entries(ACTION_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="w-40"
                placeholder="Du"
              />
              <span className="text-gray-400">→</span>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                className="w-40"
                placeholder="Au"
              />
            </div>

            {(filters.action !== 'all' || filters.dateFrom || filters.dateTo) && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Réinitialiser
              </Button>
            )}

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { fetchAuditLog(); fetchStats(); }}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualiser
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-40">Date</TableHead>
                <TableHead className="w-32">Action</TableHead>
                <TableHead>Utilisateur cible</TableHead>
                <TableHead>Changements</TableHead>
                <TableHead className="w-48">Effectué par</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Aucune entrée dans le journal d&apos;audit</p>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(entry.timestamp)}
                    </TableCell>
                    <TableCell>{getActionBadge(entry.action)}</TableCell>
                    <TableCell>
                      {entry.targetEmail ? (
                        <div>
                          <div className="font-medium text-sm">{entry.targetName}</div>
                          <div className="text-xs text-gray-500">{entry.targetEmail}</div>
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>{renderChanges(entry.changes)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="text-sm">{entry.performedByName}</div>
                          <div className="text-xs text-gray-500">{entry.performedByEmail}</div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-500">
                Affichage {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} sur {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={pagination.offset === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
